import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/*
  Readiness Actions (Design Spec 001 Phase 3).
  Consultant-controlled follow-up actions that may help clarify uncertainty,
  strengthen systems, improve evidence quality, clarify responsibility, or
  protect participant outcomes. Improvement/clarification steps only — never
  audit findings, failures, or compliance declarations. They do not change
  scoring, classification, or source verification.
*/
export function ReportReadinessActions({ actions, onAdd, onRemove }) {
  const [draft, setDraft] = useState("");

  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    onAdd(text);
    setDraft("");
  };

  return (
    <div className="space-y-3">
      <p className="text-sm leading-relaxed text-muted-foreground report-no-print">
        Consultant-controlled follow-up actions. Record practical steps that may clarify
        uncertainty, strengthen systems, improve evidence quality, clarify responsibility, or
        support participant safety, rights, dignity, choice and control, continuity of care,
        quality of supports, or lived experience. Actions are improvement steps, not findings.
        Stored in your browser session only — do not enter participant identifiers, addresses,
        medical histories, or worker private information.
      </p>

      {(!actions || actions.length === 0) ? (
        <p
          className="rounded-sm border border-dashed border-border bg-card px-4 py-3 text-sm text-muted-foreground"
          data-testid="readiness-actions-empty"
        >
          — No readiness actions recorded.
        </p>
      ) : (
        <ol className="overflow-hidden rounded-sm border border-border bg-card">
          {actions.map((action, idx) => (
            <li
              key={action.id}
              data-testid={`readiness-action-item-${action.id}`}
              className="flex items-start gap-3 border-b border-border px-4 py-3 last:border-b-0"
            >
              <span className="font-mono-label mt-0.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <p className="flex-1 text-sm leading-relaxed text-foreground">{action.text}</p>
              <button
                type="button"
                data-testid={`readiness-action-remove-${action.id}`}
                onClick={() => onRemove(action.id)}
                aria-label="Remove readiness action"
                className="report-no-print rounded-sm p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ol>
      )}

      <div className="flex items-start gap-2 report-no-print">
        <textarea
          data-testid="readiness-actions-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="e.g. Clarify who holds accountable-person responsibility for the delegation register, and confirm with the provider."
          className="flex-1 rounded-sm border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        />
        <Button
          type="button"
          data-testid="readiness-actions-add"
          onClick={submit}
          disabled={!draft.trim()}
          className="rounded-sm px-4 text-[13px] font-medium tracking-wide shadow-paper"
        >
          <Plus className="h-4 w-4" />
          Add action
        </Button>
      </div>
    </div>
  );
}
